import { cachedReferralUserId, isShortReferralCode, resolveReferralCode } from "./referral-code";
// Decodes the referrer id the app puts in `?r=` links (the `encryptText` scheme in helper-methods.ts).
// Kept apart from helper-methods so the marketing site can report `userReferrerId` without the app runtime.
export const decryptText = (textToDecrypt: string) => {
  if (isShortReferralCode(textToDecrypt)) return cachedReferralUserId(textToDecrypt) ?? "";
  if (textToDecrypt.length >= 2) {
    textToDecrypt = textToDecrypt.substring(0, textToDecrypt.length - 2);
  }

  const alphanumericChars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const maxIndex = alphanumericChars.length;

  return textToDecrypt
    .split("")
    .map((char, index) => {
      let charIndex = alphanumericChars.indexOf(char);
      if (charIndex === -1) {
        // Character not alphanumeric
        return char;
      }

      if (index < 5) {
        charIndex = (charIndex - 3 + maxIndex) % maxIndex;
      } else if (index >= 5 && index < 10) {
        charIndex = (charIndex - 1 + maxIndex) % maxIndex;
      } else {
        charIndex = (charIndex + 2) % maxIndex;
      }
      return alphanumericChars[charIndex];
    })
    .join("");
};

const REFERRER_LOCAL_STORAGE_KEY = "ref_uid";
// Values longer than this were written by the retired AES scheme (see legacy-referrer-id.ts).
const LEGACY_ENCRYPTED_REFERRER_MIN_LENGTH = 36;

/**
 * The id of the Jungle user who referred this visitor, as stored on this device, or undefined when nobody
 * did. Mirrors the anonymous branch of `trackEvent` in helper-methods.ts, including the literal "true" the
 * app stores when a referral link had no id. The legacy decoder loads on demand: it is the only reason
 * crypto-js would otherwise ship with the marketing site.
 */
export async function getStoredReferrerUserId(): Promise<string | undefined> {
  const encryptedReferrerUserId = localStorage.getItem(REFERRER_LOCAL_STORAGE_KEY);
  if (!encryptedReferrerUserId || encryptedReferrerUserId === "null") return undefined;
  if (encryptedReferrerUserId === "true") return encryptedReferrerUserId;
  if (isShortReferralCode(encryptedReferrerUserId)) return resolveReferralCode(encryptedReferrerUserId);
  if (encryptedReferrerUserId.length >= LEGACY_ENCRYPTED_REFERRER_MIN_LENGTH) {
    const { oldDecryptText } = await import("./legacy-referrer-id");
    return oldDecryptText(encryptedReferrerUserId) ?? undefined;
  }
  return decryptText(encryptedReferrerUserId);
}
